import { useMemo, useState } from 'react';
import { AlertCircle, Plus, X } from 'lucide-react';
import {
  type StepType,
  type PlanStep,
  type CreateStepRequest,
  type UpdateStepRequest,
} from '../hooks/useNetworkPlans';
import { useDevices } from '../hooks/useDevices';
import {
  type AvailableOperation,
  type VarValue,
  isCredentialValue,
  isLeftOut,
  useAvailableOperations,
  withoutLeftOut,
} from '../hooks/useDeviceActions';
import { OperationVariableInputs } from '@/components/OperationVariableInputs';
import { SiteReachDetail, SiteReachLine } from '@/components/SiteReach';
import { vlanRangesValid } from '@/utils/vlanRanges';
import { addressVariableProblem, isAddressVariable, isWholeNumberVariable } from '@/utils/netValidation';
import { formatVariableName, operationTitle } from '../utils/formatting';
import { AppSheet } from '@/components/ui/app-sheet';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { deviceTypeLabel } from '@/types/deviceType';

// `soak` is a synthetic UI type: it persists as an automated_verification step
// whose payload carries the soak spec (see handleSubmit).
type UiStepType = StepType | 'soak';

const STEP_TYPES: { value: UiStepType; label: string }[] = [
  { value: 'automated_config', label: 'Automated config' },
  { value: 'automated_verification', label: 'Automated verification' },
  { value: 'manual_task', label: 'Manual task' },
  { value: 'manual_verification', label: 'Manual verification' },
  { value: 'soak', label: 'Soak / wait' },
];

const ON_FAILURE_OPTIONS: { value: string; label: string }[] = [
  { value: 'halt', label: 'Halt — stop for the operator (default)' },
  { value: 'continue', label: 'Continue — record the failure, keep going' },
  { value: 'rollback', label: 'Roll back — undo completed steps, then halt' },
];

const NO_DEVICE = '__none__';

type VerifyType =
  | ''
  | 'device_reachable'
  | 'interface_oper_up'
  | 'lldp_neighbor'
  | 'mac_seen_on_port';

const VERIFY_TYPES: { value: VerifyType; label: string }[] = [
  { value: '', label: 'None' },
  { value: 'device_reachable', label: 'Device is reachable' },
  { value: 'interface_oper_up', label: 'Interface is up' },
  { value: 'lldp_neighbor', label: 'LLDP neighbor present' },
  { value: 'mac_seen_on_port', label: 'MAC seen on device/port' },
];

function isAutomatedType(t: UiStepType): boolean {
  return t === 'automated_config' || t === 'automated_verification';
}

function isManualType(t: UiStepType): boolean {
  return t === 'manual_task' || t === 'manual_verification';
}

// Auto-verify is meaningful on verification / manual steps, but not on
// automated_config (where it would replace the config push) or a soak step.
function supportsVerification(t: UiStepType): boolean {
  return t !== 'automated_config' && t !== 'soak';
}

interface AddStepModalProps {
  isActive: boolean;
  onClose: () => void;
  onSave: (data: CreateStepRequest | UpdateStepRequest, stepId?: string) => Promise<void>;
  editingStep: PlanStep | null;
  organizationId: string;
  isSaving: boolean;
}

export default function AddStepModal({
  isActive,
  onClose,
  onSave,
  editingStep,
  organizationId,
  isSaving,
}: AddStepModalProps) {
  const [stepType, setStepType] = useState<UiStepType>('automated_config');
  const [onFailure, setOnFailure] = useState('halt');
  const [soakDuration, setSoakDuration] = useState('');
  const [stepTitle, setStepTitle] = useState('');
  const [titleManuallyEdited, setTitleManuallyEdited] = useState(false);
  const [stepDescription, setStepDescription] = useState('');
  const [expectedAlertsText, setExpectedAlertsText] = useState('');
  const [expectedDurationText, setExpectedDurationText] = useState('');
  const [checklistItems, setChecklistItems] = useState<{ id?: string; text: string }[]>([]);
  const [stepError, setStepError] = useState<string | null>(null);

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | undefined>(undefined);
  const [selectedOperation, setSelectedOperation] = useState<AvailableOperation | null>(null);
  const [variables, setVariables] = useState<Record<string, VarValue>>({});
  const [multiVariables, setMultiVariables] = useState<Record<string, string[]>>({});
  const [deviceFilter, setDeviceFilter] = useState('');

  // Telemetry-verification condition (auto_verify).
  const [verifyType, setVerifyType] = useState<VerifyType>('');
  const [verifyDeviceId, setVerifyDeviceId] = useState<string | undefined>(undefined);
  const [verifyInterface, setVerifyInterface] = useState('');
  const [verifyNeighborId, setVerifyNeighborId] = useState<string | undefined>(undefined);
  const [verifyMac, setVerifyMac] = useState('');

  // Devices are needed both for automated operations and for the telemetry
  // verification condition (which can target any device), so load them whenever
  // the modal is open.
  const { data: devices, isLoading: isLoadingDevices } = useDevices(
    isActive ? organizationId : undefined,
  );
  const { data: operations, isLoading: isLoadingOperations } = useAvailableOperations(
    isActive && isAutomatedType(stepType) ? selectedDeviceId : undefined,
  );

  const resetVerification = () => {
    setVerifyType('');
    setVerifyDeviceId(undefined);
    setVerifyInterface('');
    setVerifyNeighborId(undefined);
    setVerifyMac('');
  };

  const loadVerification = (av: Record<string, unknown> | null) => {
    if (av && typeof av.type === 'string') {
      setVerifyType(av.type as VerifyType);
      setVerifyDeviceId(typeof av.deviceId === 'string' ? av.deviceId : undefined);
      setVerifyInterface(typeof av.interface === 'string' ? av.interface : '');
      setVerifyNeighborId(typeof av.neighborDeviceId === 'string' ? av.neighborDeviceId : undefined);
      setVerifyMac(typeof av.mac === 'string' ? av.mac : '');
    } else {
      resetVerification();
    }
  };

  // Reset the form each time the modal opens, and if it is handed another step while
  // open. Done during render rather than in an effect, so the stale form never paints.
  // `undefined` while closed is what makes a reopen count as a change even for the
  // same step; `null` is an open modal creating a new one.
  const openedFor = isActive ? editingStep : undefined;
  const [formFor, setFormFor] = useState<PlanStep | null | undefined>(undefined);
  if (openedFor !== formFor) {
    setFormFor(openedFor);
    if (openedFor) {
      const step = openedFor;
      // A soak step persists as automated_verification + payload.soak.
      const soakSpec = (step.payload as { soak?: { durationMinutes?: number } }).soak;
      const isSoakStep = step.stepType === 'automated_verification' && soakSpec != null;
      setStepType(isSoakStep ? 'soak' : step.stepType);
      setSoakDuration(soakSpec?.durationMinutes != null ? String(soakSpec.durationMinutes) : '');
      setOnFailure(step.onFailure || 'halt');
      setStepTitle(step.title);
      setStepDescription(step.description || '');
      setExpectedAlertsText((step.expectedAlerts ?? []).join('\n'));
      setExpectedDurationText(
        step.expectedDurationMinutes != null ? String(step.expectedDurationMinutes) : '',
      );
      setChecklistItems((step.checklist ?? []).map((c) => ({ id: c.id, text: c.text })));
      loadVerification(isSoakStep ? null : step.autoVerify);
      setTitleManuallyEdited(true);
      setStepError(null);
      if (isAutomatedType(step.stepType) && step.deviceId) {
        setSelectedDeviceId(step.deviceId);
        const payload = step.payload as {
          operations?: { operationName: string; variables?: Record<string, VarValue> }[];
        };
        if (payload.operations?.[0]) {
          setVariables(payload.operations[0].variables ?? {});
        }
        setMultiVariables({});
      } else {
        setSelectedDeviceId(undefined);
        setSelectedOperation(null);
        setVariables({});
        setMultiVariables({});
      }
      setDeviceFilter('');
    } else if (openedFor === null) {
      setStepType('automated_config');
      setOnFailure('halt');
      setSoakDuration('');
      setStepTitle('');
      setTitleManuallyEdited(false);
      setStepDescription('');
      setExpectedAlertsText('');
      setExpectedDurationText('');
      setChecklistItems([]);
      setStepError(null);
      setSelectedDeviceId(undefined);
      setSelectedOperation(null);
      setVariables({});
      setMultiVariables({});
      setDeviceFilter('');
      resetVerification();
    }
  }

  // Select the edited step's operation once the device's operations load. Only when
  // either input changes — re-applying it on every render would undo the user's pick.
  const [operationSeed, setOperationSeed] = useState<{
    operations: typeof operations;
    step: PlanStep | null;
  }>({ operations: undefined, step: null });
  if (operationSeed.operations !== operations || operationSeed.step !== editingStep) {
    setOperationSeed({ operations, step: editingStep });
    const opName = (
      editingStep?.payload as
        | { operations?: { operationName: string; variables?: Record<string, VarValue> }[] }
        | undefined
    )?.operations?.[0]?.operationName;
    const match = opName ? operations?.find((o) => o.operationName === opName) : undefined;
    if (match) setSelectedOperation(match);
  }

  // Until the user types their own, the title follows the chosen operation and device.
  const autoTitle = (() => {
    if (titleManuallyEdited || !selectedOperation) return null;
    const device = devices?.find((d) => d.id === selectedDeviceId);
    const opLabel = operationTitle(selectedOperation);
    const deviceLabel = device?.name || 'Unknown Device';
    const multiVar = selectedOperation.variables.find((v) => v.multiSelect && v.possibleValues);
    const multiCount = multiVar ? (multiVariables[multiVar.name] ?? []).length : 0;
    return multiCount > 1 && multiVar
      ? `${opLabel} on ${deviceLabel} (${String(multiCount)} ${formatVariableName(multiVar.name)}s)`
      : `${opLabel} on ${deviceLabel}`;
  })();
  if (autoTitle !== null && autoTitle !== stepTitle) setStepTitle(autoTitle);

  const handleStepTypeChange = (newType: UiStepType) => {
    setStepType(newType);
    if (!isAutomatedType(newType)) {
      setSelectedDeviceId(undefined);
      setSelectedOperation(null);
      setVariables({});
      setMultiVariables({});
      setDeviceFilter('');
    }
  };

  const handleDeviceChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId === NO_DEVICE ? undefined : deviceId);
    setSelectedOperation(null);
    setVariables({});
    setMultiVariables({});
    setTitleManuallyEdited(false);
  };

  const handleSelectOperation = (op: AvailableOperation) => {
    setSelectedOperation(op);
    const defaults: Record<string, VarValue> = {};
    const multiDefaults: Record<string, string[]> = {};
    for (const v of op.variables) {
      if (v.multiSelect && v.possibleValues) multiDefaults[v.name] = [];
      else if (v.varType === 'vlan_range_list') defaults[v.name] = [];
      else defaults[v.name] = v.default ?? '';
    }
    setVariables(defaults);
    setMultiVariables(multiDefaults);
    setTitleManuallyEdited(false);
  };

  const showVerification = supportsVerification(stepType);

  const buildAutoVerify = (): Record<string, unknown> => {
    if (!showVerification || !verifyType || !verifyDeviceId) return {};
    const c: Record<string, unknown> = { type: verifyType, deviceId: verifyDeviceId };
    if (verifyType === 'interface_oper_up') c.interface = verifyInterface.trim();
    if (verifyType === 'lldp_neighbor') c.neighborDeviceId = verifyNeighborId;
    if (verifyType === 'mac_seen_on_port') {
      c.mac = verifyMac.trim();
      if (verifyInterface.trim()) c.interface = verifyInterface.trim();
    }
    return c;
  };

  const isVerificationValid = (): boolean => {
    if (!showVerification || !verifyType) return true;
    if (!verifyDeviceId) return false;
    if (verifyType === 'interface_oper_up') return verifyInterface.trim() !== '';
    if (verifyType === 'lldp_neighbor') return !!verifyNeighborId;
    if (verifyType === 'mac_seen_on_port') return verifyMac.trim() !== '';
    return true;
  };

  const isSoak = stepType === 'soak';
  const soakValid = (): boolean => {
    const n = Number(soakDuration.trim());
    return soakDuration.trim() !== '' && Number.isFinite(n) && n > 0;
  };

  const isFormValid = (): boolean => {
    if (!stepTitle.trim()) return false;
    if (isSoak) return soakValid();
    if (!isVerificationValid()) return false;
    if (isAutomatedType(stepType)) {
      if (!selectedDeviceId || !selectedOperation) return false;
      return selectedOperation.variables.every((v) => {
        if (isLeftOut(v, variables[v.name])) return true;
        if (v.multiSelect && v.possibleValues) {
          return (multiVariables[v.name] ?? []).length > 0;
        }
        if (v.varType === 'credential') return isCredentialValue(variables[v.name]);
        if (v.varType === 'vlan_range_list') {
          const val = variables[v.name];
          return Array.isArray(val) && vlanRangesValid(val);
        }
        if (isAddressVariable(v.varType)) {
          const val = variables[v.name];
          return typeof val === 'string' && addressVariableProblem(v.varType, val, v.family) === null;
        }
        const val = variables[v.name] ?? '';
        if (typeof val !== 'string' || val.trim() === '') return false;
        if (isWholeNumberVariable(v.varType)) {
          const num = Number(val);
          if (!Number.isInteger(num)) return false;
          if (v.min !== null && num < v.min) return false;
          if (v.max !== null && num > v.max) return false;
        }
        return true;
      });
    }
    return true;
  };

  const buildOperations = () => {
    if (!selectedOperation) return [];
    const sent = withoutLeftOut(selectedOperation.variables, variables);
    const multiSelectVars = selectedOperation.variables.filter(
      (v) => v.multiSelect && v.possibleValues && (multiVariables[v.name] ?? []).length > 0,
    );
    if (multiSelectVars.length === 0) {
      return [{ operationName: selectedOperation.operationName, variables: sent }];
    }
    const multiVar = multiSelectVars[0];
    const selectedValues = multiVariables[multiVar.name] ?? [];
    return selectedValues.map((val) => ({
      operationName: selectedOperation.operationName,
      variables: { ...sent, [multiVar.name]: val },
    }));
  };

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setStepError(null);
    if (!stepTitle.trim()) {
      setStepError('Title is required');
      return;
    }
    const expectedAlerts = expectedAlertsText
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    const durationNum = Number(expectedDurationText.trim());
    const expectedDurationMinutes =
      isManualType(stepType) &&
      expectedDurationText.trim() !== '' &&
      Number.isFinite(durationNum) &&
      durationNum > 0
        ? Math.round(durationNum)
        : undefined;
    const checklist = isManualType(stepType)
      ? checklistItems
          .filter((c) => c.text.trim())
          .map((c) => ({ ...(c.id ? { id: c.id } : {}), text: c.text.trim() }))
      : undefined;

    // A soak step persists as automated_verification + soakMinutes.
    const realStepType: StepType = isSoak ? 'automated_verification' : stepType;
    const automatedFields =
      isAutomatedType(stepType) && !isSoak && selectedDeviceId && selectedOperation
        ? { deviceId: selectedDeviceId, payload: { operations: buildOperations() } }
        : {};

    const data: CreateStepRequest & UpdateStepRequest = {
      stepType: realStepType,
      title: stepTitle,
      description: stepDescription || undefined,
      expectedAlerts,
      autoVerify: buildAutoVerify(),
      ...(expectedDurationMinutes !== undefined ? { expectedDurationMinutes } : {}),
      ...(checklist !== undefined ? { checklist } : {}),
      ...(isAutomatedType(stepType) || isSoak ? { onFailure } : {}),
      ...(isSoak ? { soakMinutes: Math.round(Number(soakDuration.trim())) } : {}),
      ...automatedFields,
    };
    try {
      if (editingStep) {
        await onSave(data, editingStep.id);
      } else {
        await onSave(data);
      }
    } catch {
      setStepError('Failed to save step.');
    }
  };

  const filteredDevices = useMemo(() => {
    if (!devices) return [];
    if (!deviceFilter.trim()) return devices;
    const lower = deviceFilter.toLowerCase();
    return devices.filter(
      (d) =>
        d.name.toLowerCase().includes(lower) ||
        (d.ipAddress && d.ipAddress.toLowerCase().includes(lower)),
    );
  }, [devices, deviceFilter]);

  const isAutomated = isAutomatedType(stepType);
  const isManual = isManualType(stepType);

  return (
    <AppSheet
      open={isActive}
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={editingStep ? 'Edit step' : 'Add step'}
      size="lg"
    >
      <AppSheet.Body>
        <form
          id="add-step-form"
          onSubmit={(e) => void handleSubmit(e)}
          className="space-y-4"
        >
          {stepError ? (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{stepError}</AlertDescription>
            </Alert>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="as-step-type">Step type *</Label>
            <Select
              value={stepType}
              onValueChange={(v) => { handleStepTypeChange(v as UiStepType); }}
            >
              <SelectTrigger id="as-step-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STEP_TYPES.map((st) => (
                  <SelectItem key={st.value} value={st.value}>
                    {st.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isSoak ? (
            <div className="space-y-1.5">
              <Label htmlFor="as-soak-duration">Soak duration (minutes) *</Label>
              <Input
                id="as-soak-duration"
                type="number"
                min={1}
                value={soakDuration}
                onChange={(e) => { setSoakDuration(e.target.value); }}
                placeholder="e.g., 15"
              />
              <p className="text-xs text-muted-foreground">
                Execution waits this long watching the change&apos;s devices, then proceeds only
                if no unexpected alerts fired — otherwise it halts and notifies.
              </p>
            </div>
          ) : null}

          {isAutomated || isSoak ? (
            <div className="space-y-1.5">
              <Label htmlFor="as-on-failure">On failure</Label>
              <Select value={onFailure} onValueChange={setOnFailure}>
                <SelectTrigger id="as-on-failure">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ON_FAILURE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {isAutomated ? (
            <div className="space-y-1.5">
              <Label htmlFor="as-device">Device *</Label>
              {isLoadingDevices ? (
                <Progress value={undefined} className="h-1" />
              ) : (
                <>
                  {devices && devices.length > 10 ? (
                    <Input
                      type="text"
                      placeholder="Filter devices…"
                      value={deviceFilter}
                      onChange={(e) => { setDeviceFilter(e.target.value); }}
                      className="mb-2"
                    />
                  ) : null}
                  <Select
                    value={selectedDeviceId ?? NO_DEVICE}
                    onValueChange={handleDeviceChange}
                  >
                    <SelectTrigger id="as-device">
                      <SelectValue placeholder="Select a device…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_DEVICE}>Select a device…</SelectItem>
                      {filteredDevices.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                          {d.ipAddress ? ` (${d.ipAddress})` : ` (${deviceTypeLabel(d.deviceType)})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          ) : null}

          {isAutomated && selectedDeviceId ? (
            <div className="space-y-1.5">
              <Label>Operation *</Label>
              {isLoadingOperations ? (
                <Progress value={undefined} className="h-1" />
              ) : operations && operations.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {operations.map((op) => {
                    const isSelected =
                      selectedOperation?.operationName === op.operationName;
                    return (
                      <button
                        key={op.operationName}
                        type="button"
                        className={cn(
                          'text-left rounded-md border p-3 transition-colors',
                          isSelected
                            ? 'border-sev-5 bg-sev-5/10 ring-1 ring-sev-5'
                            : 'border-border-subtle hover:border-border hover:bg-accent/40',
                        )}
                        onClick={() => { handleSelectOperation(op); }}
                      >
                        <p className="text-sm font-semibold">
                          {operationTitle(op)}
                        </p>
                        {op.site ? <SiteReachLine site={op.site} className="mt-0.5" /> : null}
                        {op.description ? (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {op.description}
                          </p>
                        ) : null}
                        {op.variables.length > 0 ? (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {op.variables.map((v) => (
                              <Badge
                                key={v.name}
                                variant="secondary"
                                className="font-normal text-[10px]"
                              >
                                {formatVariableName(v.name)}
                              </Badge>
                            ))}
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  No operations available for this device.
                </p>
              )}
            </div>
          ) : null}

          {isAutomated && selectedOperation?.site ? (
            <SiteReachDetail site={selectedOperation.site} />
          ) : null}

          {isAutomated && selectedOperation && selectedOperation.variables.length > 0 ? (
            <div className="rounded-md border border-border-subtle bg-muted/30 p-3 space-y-3">
              <p className="text-sm font-semibold">Variables</p>
              <OperationVariableInputs
                variables={selectedOperation.variables}
                values={variables}
                onValuesChange={setVariables}
                multiValues={multiVariables}
                onMultiChange={setMultiVariables}
                idPrefix="step-var"
              />
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="as-title">Title *</Label>
            <Input
              id="as-title"
              type="text"
              value={stepTitle}
              onChange={(e) => {
                setStepTitle(e.target.value);
                setTitleManuallyEdited(true);
              }}
              placeholder={
                isAutomated
                  ? 'Auto-generated from device + operation'
                  : 'e.g., Verify VLAN propagation'
              }
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="as-description">Description</Label>
            <Textarea
              id="as-description"
              value={stepDescription}
              onChange={(e) => { setStepDescription(e.target.value); }}
              placeholder="Detailed instructions or notes for this step"
              rows={3}
            />
          </div>

          {isManual ? (
            <div className="space-y-1.5">
              <Label htmlFor="as-expected-duration">
                Expected duration{' '}
                <span className="text-muted-foreground font-normal">(minutes, optional)</span>
              </Label>
              <Input
                id="as-expected-duration"
                type="number"
                min={1}
                value={expectedDurationText}
                onChange={(e) => { setExpectedDurationText(e.target.value); }}
                placeholder="e.g., 30"
              />
              <p className="text-xs text-muted-foreground">
                If the step is still in progress this long after it starts, the change&apos;s owners
                and assignees are notified that it&apos;s overdue.
              </p>
            </div>
          ) : null}

          {isManual ? (
            <div className="space-y-1.5">
              <Label>
                Checklist <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              {checklistItems.length > 0 ? (
                <div className="space-y-1.5">
                  {checklistItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={item.text}
                        onChange={(e) => {
                          const next = [...checklistItems];
                          next[idx] = { ...next[idx], text: e.target.value };
                          setChecklistItems(next);
                        }}
                        placeholder={`Item ${String(idx + 1)}`}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() => { setChecklistItems(checklistItems.filter((_, i) => i !== idx)); }}
                        aria-label="Remove item"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => { setChecklistItems([...checklistItems, { text: '' }]); }}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add checklist item
              </Button>
              <p className="text-xs text-muted-foreground">
                Sub-tasks the operator ticks off while running this step.
              </p>
            </div>
          ) : null}

          {showVerification ? (
            <div className="space-y-3 rounded-md border border-border-subtle bg-muted/30 p-3">
              <div className="space-y-1.5">
                <Label htmlFor="as-verify-type">
                  Telemetry verification{' '}
                  <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Select
                  value={verifyType || '__none__'}
                  onValueChange={(v) => { setVerifyType(v === '__none__' ? '' : (v as VerifyType)); }}
                >
                  <SelectTrigger id="as-verify-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VERIFY_TYPES.map((vt) => (
                      <SelectItem key={vt.value || '__none__'} value={vt.value || '__none__'}>
                        {vt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Confirm the physical work landed using what the network reports. The step can
                  only be completed once telemetry agrees.
                </p>
              </div>

              {verifyType ? (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="as-verify-device">
                      {verifyType === 'lldp_neighbor' ? 'Local device *' : 'Device *'}
                    </Label>
                    <Select
                      value={verifyDeviceId ?? NO_DEVICE}
                      onValueChange={(v) => { setVerifyDeviceId(v === NO_DEVICE ? undefined : v); }}
                    >
                      <SelectTrigger id="as-verify-device">
                        <SelectValue placeholder="Select a device…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_DEVICE}>Select a device…</SelectItem>
                        {(devices ?? []).map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                            {d.ipAddress ? ` (${d.ipAddress})` : ` (${deviceTypeLabel(d.deviceType)})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {verifyType === 'lldp_neighbor' ? (
                    <div className="space-y-1.5">
                      <Label htmlFor="as-verify-neighbor">Expected neighbor *</Label>
                      <Select
                        value={verifyNeighborId ?? NO_DEVICE}
                        onValueChange={(v) => { setVerifyNeighborId(v === NO_DEVICE ? undefined : v); }}
                      >
                        <SelectTrigger id="as-verify-neighbor">
                          <SelectValue placeholder="Select the neighbor device…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_DEVICE}>Select a device…</SelectItem>
                          {(devices ?? [])
                            .filter((d) => d.id !== verifyDeviceId)
                            .map((d) => (
                              <SelectItem key={d.id} value={d.id}>
                                {d.name}
                                {d.ipAddress ? ` (${d.ipAddress})` : ` (${deviceTypeLabel(d.deviceType)})`}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : null}

                  {verifyType === 'interface_oper_up' ? (
                    <div className="space-y-1.5">
                      <Label htmlFor="as-verify-interface">Interface name *</Label>
                      <Input
                        id="as-verify-interface"
                        type="text"
                        value={verifyInterface}
                        onChange={(e) => { setVerifyInterface(e.target.value); }}
                        placeholder="e.g., GigabitEthernet0/24"
                        className="font-mono"
                      />
                    </div>
                  ) : null}

                  {verifyType === 'mac_seen_on_port' ? (
                    <>
                      <div className="space-y-1.5">
                        <Label htmlFor="as-verify-mac">MAC address *</Label>
                        <Input
                          id="as-verify-mac"
                          type="text"
                          value={verifyMac}
                          onChange={(e) => { setVerifyMac(e.target.value); }}
                          placeholder="aa:bb:cc:dd:ee:ff"
                          className="font-mono"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="as-verify-mac-iface">
                          Interface{' '}
                          <span className="text-muted-foreground font-normal">(optional)</span>
                        </Label>
                        <Input
                          id="as-verify-mac-iface"
                          type="text"
                          value={verifyInterface}
                          onChange={(e) => { setVerifyInterface(e.target.value); }}
                          placeholder="Restrict to a specific port"
                          className="font-mono"
                        />
                      </div>
                    </>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="as-expected-alerts">
              Expected alerts <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="as-expected-alerts"
              value={expectedAlertsText}
              onChange={(e) => { setExpectedAlertsText(e.target.value); }}
              placeholder={'interface_oper_down*\nstp_*'}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">
              Glob patterns (one per line) matched against an alert&apos;s key. Alerts this
              step is expected to cause are marked <em>expected</em> and not paged — so only
              genuine surprises show as unexpected during the change.
            </p>
          </div>
        </form>
      </AppSheet.Body>
      <AppSheet.Footer>
        <Button variant="outline" type="button" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="add-step-form"
          disabled={!isFormValid() || isSaving}
        >
          {isSaving ? 'Saving…' : editingStep ? 'Save' : 'Add step'}
        </Button>
      </AppSheet.Footer>
    </AppSheet>
  );
}
